import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Panel } from '../components/Panel'
import { Button } from '../components/Button'
import { Field, Select } from '../components/Field'
import { ApiError, api, type EmailTemplate, type MeResponse, type TemplatePreview } from '../api/client'

const TEST_EMAIL_LS_KEY = 'aamcirc.test_email'

const PUB_OPTIONS = [
  { value: '', label: 'Multi-title (no publication)' },
  { value: '1', label: 'RC Car Action' },
  { value: '2', label: 'Model Airplane News' },
  { value: '3', label: 'Flight Journal' },
  { value: '4', label: 'Die Cast X' },
]

const PURPOSE_OPTIONS = [
  { value: 'renewal', label: 'Renewal (renewals_domain)' },
  { value: 'welcome', label: 'Welcome (membership_domain)' },
  { value: 'winback', label: 'Win-back (marketing_domain)' },
  { value: 'marketing', label: 'Marketing (membership_domain)' },
  { value: 'transactional', label: 'Transactional (membership_domain)' },
]

type FormState = {
  slug: string
  name: string
  purpose: EmailTemplate['purpose']
  publication_id: string
  subject: string
  preheader: string
  html_body: string
  text_body: string
  sample_vars_json: string
  variable_defaults_json: string
  enabled: boolean
}

function emptyForm(): FormState {
  return {
    slug: '',
    name: '',
    purpose: 'renewal',
    publication_id: '',
    subject: '',
    preheader: '',
    html_body: '<h1>Hi {{ first_name }}</h1>\n<p>Your subscription to {{ publication.name }} ends {{ subscription.current_period_end }}.</p>\n<p><a href="{{ renewal_link }}">Renew now</a></p>',
    text_body: '',
    sample_vars_json: '{}',
    variable_defaults_json: '{}',
    enabled: true,
  }
}

export function TemplateEditorPage() {
  const { id } = useParams<{ id: string }>()
  const isNew = !id || id === 'new'
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const existing = useQuery({
    queryKey: ['template', id],
    queryFn: () => api.get<EmailTemplate>(`/api/templates/${id}`),
    enabled: !isNew,
  })

  const [form, setForm] = useState<FormState>(emptyForm())
  const [previewEmail, setPreviewEmail] = useState('')
  useEffect(() => {
    if (existing.data) {
      setForm({
        slug: existing.data.slug,
        name: existing.data.name,
        purpose: existing.data.purpose,
        publication_id: existing.data.publication_id?.toString() ?? '',
        subject: existing.data.subject,
        preheader: existing.data.preheader ?? '',
        html_body: existing.data.html_body,
        text_body: existing.data.text_body ?? '',
        sample_vars_json: JSON.stringify(existing.data.sample_vars ?? {}, null, 2),
        variable_defaults_json: JSON.stringify(existing.data.variable_defaults ?? {}, null, 2),
        enabled: existing.data.enabled,
      })
    }
  }, [existing.data])

  const body = useMemo(() => ({
    slug: form.slug,
    name: form.name,
    purpose: form.purpose,
    publication_id: form.publication_id ? Number(form.publication_id) : null,
    subject: form.subject,
    preheader: form.preheader || null,
    html_body: form.html_body,
    text_body: form.text_body || null,
    sample_vars: safeParseJson(form.sample_vars_json),
    variable_defaults: safeParseJson(form.variable_defaults_json),
    enabled: form.enabled,
  }), [form])

  const save = useMutation({
    mutationFn: async () => {
      if (isNew) return api.post<EmailTemplate>('/api/templates', body)
      return api.put<EmailTemplate>(`/api/templates/${id}`, body)
    },
    onSuccess: (t) => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
      // Detail cache too — so re-opening the same template shows persisted
      // state, not whatever was last cached client-side.
      queryClient.invalidateQueries({ queryKey: ['template', String(t.id)] })
      if (isNew) navigate(`/templates/${t.id}`, { replace: true })
    },
  })

  const clone = useMutation({
    mutationFn: () => api.post<EmailTemplate>(`/api/templates/${id}/clone`, {}),
    onSuccess: (t) => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
      navigate(`/templates/${t.id}`)
    },
  })

  // Live preview — debounce 500ms, only when we have an id
  const [previewTrigger, setPreviewTrigger] = useState(0)
  useEffect(() => {
    const h = setTimeout(() => setPreviewTrigger((n) => n + 1), 500)
    return () => clearTimeout(h)
  }, [form.subject, form.html_body, form.text_body, form.sample_vars_json, form.variable_defaults_json, form.publication_id, form.purpose, previewEmail])

  const preview = useQuery({
    queryKey: ['template-preview', id, previewTrigger],
    queryFn: () => api.post<TemplatePreview>(`/api/templates/${id}/preview`, {
      sample_vars: safeParseJson(form.sample_vars_json),
      to_email: previewEmail || null,
      // Send the unsaved form draft so the preview renders live-edit state
      // without requiring a Save.
      subject: form.subject,
      html_body: form.html_body,
      text_body: form.text_body || null,
      publication_id: form.publication_id ? Number(form.publication_id) : null,
      variable_defaults: safeParseJson(form.variable_defaults_json),
    }),
    enabled: !isNew && !!id,
  })

  // Auto-size the preview iframe to its content so operators don't have to
  // scroll inside it. Re-measures on each srcdoc load (images can push the
  // height once they finish decoding, so we also poll briefly after load).
  const previewIframeRef = useRef<HTMLIFrameElement | null>(null)
  const [previewHeight, setPreviewHeight] = useState(600)
  const handlePreviewLoad = () => {
    const measure = () => {
      const doc = previewIframeRef.current?.contentDocument
      if (!doc || !doc.body) return
      const h = Math.max(
        doc.body.scrollHeight,
        doc.documentElement.scrollHeight,
      )
      if (h > 0) setPreviewHeight(Math.max(600, h + 16))
    }
    measure()
    // Images (especially first-paint data URLs) can change the final height
    // after the initial load event fires; sample a few times to catch that.
    const ticks = [80, 240, 600].map((ms) => window.setTimeout(measure, ms))
    // Cleanup is best-effort — the next load event will reset us anyway.
    return () => ticks.forEach(window.clearTimeout)
  }

  // Test send modal — default to the logged-in user's email, then remember
  // whatever the operator actually uses so subsequent opens don't re-type.
  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<MeResponse>('/api/me'),
    staleTime: 60_000,
  })
  const [testOpen, setTestOpen] = useState(false)
  const [testEmail, setTestEmail] = useState('')
  useEffect(() => {
    if (testEmail) return
    const remembered = typeof window !== 'undefined' ? window.localStorage.getItem(TEST_EMAIL_LS_KEY) : null
    setTestEmail(remembered || me.data?.email || '')
  }, [me.data, testEmail])
  useEffect(() => {
    if (previewEmail) return
    const remembered = typeof window !== 'undefined' ? window.localStorage.getItem(TEST_EMAIL_LS_KEY) : null
    setPreviewEmail(remembered || me.data?.email || '')
  }, [me.data, previewEmail])
  const sendTest = useMutation({
    mutationFn: () => {
      if (typeof window !== 'undefined' && testEmail) {
        window.localStorage.setItem(TEST_EMAIL_LS_KEY, testEmail)
      }
      return api.post<{
        ok: boolean
        mailgun_message_id: string
        data_source: 'real_full' | 'real_subscriber_only' | 'fake'
        subscriber_id: number | null
        subscription_id: number | null
        personalized_links: boolean
      }>(
        `/api/templates/${id}/test-send`,
        { to_email: testEmail, sample_vars: safeParseJson(form.sample_vars_json) },
      )
    },
  })

  const saveError = describeError(save.error)
  const testError = describeError(sendTest.error)

  return (
    <div className="p-8 space-y-6 max-w-[1600px]">
      <div className="flex items-center gap-3">
        <Link to="/templates" className="label-amber hover:underline">← Templates</Link>
        <span className="label text-paper-trace">/</span>
        <span className="label">{isNew ? 'New' : `Template #${id}`}</span>
      </div>

      <header className="flex items-end justify-between border-b border-ink-500 pb-6">
        <h1 className="font-serif text-[32px] leading-tight text-paper">
          {isNew ? 'New template' : form.name || form.slug}
        </h1>
        <div className="flex items-center gap-3">
          {!isNew && (
            <Button
              variant="outline"
              size="sm"
              title="Copy the saved version of this template under a new slug"
              disabled={clone.isPending}
              onClick={() => clone.mutate()}
            >
              {clone.isPending ? 'Cloning…' : 'Clone'}
            </Button>
          )}
          {!isNew && (
            <Button variant="outline" size="sm" onClick={() => setTestOpen(true)}>
              Send test
            </Button>
          )}
          <Button
            size="md"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </header>

      {saveError && (
        <div className="border border-radar-red/60 bg-radar-red/10 text-radar-red font-mono text-[12px] p-3">
          {saveError}
        </div>
      )}
      {save.isSuccess && !save.isPending && (
        <div className="border border-radar-green/60 bg-radar-green/10 text-radar-green font-mono text-[12px] p-3">
          ✓ Saved · template #{save.data.id} · {new Date(save.data.updated_at ?? Date.now()).toLocaleTimeString()}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6">
        {/* LEFT: metadata + body */}
        <div className="space-y-4">
          <Panel label="Metadata" accent="amber">
            <div className="space-y-3">
              <Field
                label="Slug"
                value={form.slug}
                onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                placeholder="e.g. fj-renewal-3mo"
              />
              <Field
                label="Name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Internal label"
              />
              <Select
                label="Purpose"
                value={form.purpose}
                onChange={(e) => setForm((f) => ({ ...f, purpose: e.target.value as EmailTemplate['purpose'] }))}
                options={PURPOSE_OPTIONS}
              />
              <Select
                label="Publication"
                value={form.publication_id}
                onChange={(e) => setForm((f) => ({ ...f, publication_id: e.target.value }))}
                options={PUB_OPTIONS}
              />
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                />
                <span className="label">Enabled</span>
              </label>
            </div>
          </Panel>

          <Panel label="Content">
            <div className="space-y-3">
              <Field
                label="Subject"
                value={form.subject}
                onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                placeholder="Renew your {{ publication.name }} · {{ first_name }}"
              />
              <Field
                label="Preheader"
                value={form.preheader}
                onChange={(e) => setForm((f) => ({ ...f, preheader: e.target.value }))}
                placeholder="Optional inbox-preview text"
              />
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="label">HTML body</span>
                  <label className="cursor-pointer label-amber hover:underline">
                    Upload .html file
                    <input
                      type="file"
                      accept=".html,.htm,text/html"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0]
                        if (!file) return
                        const text = await file.text()
                        setForm((f) => ({ ...f, html_body: text }))
                        e.target.value = ''
                      }}
                    />
                  </label>
                </div>
                <textarea
                  value={form.html_body}
                  onChange={(e) => setForm((f) => ({ ...f, html_body: e.target.value }))}
                  className="w-full bg-ink-700/60 border border-ink-500 font-mono text-[12px] text-paper p-3 h-[360px] focus:outline-none focus:border-amber-glow"
                  spellCheck={false}
                />
              </div>
              <label className="block">
                <span className="label mb-1.5 block">Text body (optional)</span>
                <textarea
                  value={form.text_body}
                  onChange={(e) => setForm((f) => ({ ...f, text_body: e.target.value }))}
                  className="w-full bg-ink-700/60 border border-ink-500 font-mono text-[12px] text-paper p-3 h-[120px] focus:outline-none focus:border-amber-glow"
                  spellCheck={false}
                  placeholder="Mailgun auto-derives from HTML if empty."
                />
              </label>
              <label className="block">
                <span className="label mb-1.5 block">Sample merge vars (JSON)</span>
                <textarea
                  value={form.sample_vars_json}
                  onChange={(e) => setForm((f) => ({ ...f, sample_vars_json: e.target.value }))}
                  className="w-full bg-ink-700/60 border border-ink-500 font-mono text-[12px] text-paper p-3 h-[100px] focus:outline-none focus:border-amber-glow"
                  spellCheck={false}
                  placeholder={'{"first_name": "Jane", "renewal_link": "https://..."}'}
                />
              </label>
              <label className="block">
                <span className="label mb-1.5 block">Variable defaults (JSON)</span>
                <textarea
                  value={form.variable_defaults_json}
                  onChange={(e) => setForm((f) => ({ ...f, variable_defaults_json: e.target.value }))}
                  className="w-full bg-ink-700/60 border border-ink-500 font-mono text-[12px] text-paper p-3 h-[100px] focus:outline-none focus:border-amber-glow"
                  spellCheck={false}
                  placeholder={'{"renewal_link": "https://.../subscribe", "first_name": "friend"}'}
                />
                <span className="font-mono text-[10px] text-paper-trace mt-1 block">
                  Fallback values used when a recipient's field is null/empty.
                  Applied at send time — a variable with a default here will
                  never cause a skipped_variable_null row. Supports dotted
                  keys for nested fields (e.g. <code>subscription.current_period_end</code>).
                </span>
              </label>
            </div>
          </Panel>
        </div>

        {/* RIGHT: live preview */}
        <div className="space-y-4">
          <Panel label="Live preview" accent="cyan">
            <div className="p-4 border-b border-ink-500 space-y-2">
              <Field
                label="Preview as subscriber email"
                type="email"
                value={previewEmail}
                onChange={(e) => setPreviewEmail(e.target.value)}
                placeholder="member@example.com"
              />
              {preview.data && (
                <div className="font-mono text-[10px] text-paper-dim">
                  {preview.data.subscriber_id ? (
                    <>
                      Previewing for <span className="text-paper">{preview.data.preview_email}</span>
                      {' '}· subscriber #{preview.data.subscriber_id}
                      {preview.data.subscription_id && ` · subscription #${preview.data.subscription_id}`}
                      {preview.data.personalized_links && ' · personalized links active'}
                    </>
                  ) : previewEmail ? (
                    <>No subscriber matched <span className="text-paper">{previewEmail}</span> · using sample data</>
                  ) : (
                    <>Using sample data · enter a subscriber email for a personalized preview</>
                  )}
                </div>
              )}
            </div>
            {isNew ? (
              <div className="label text-paper-trace p-6">Save first to see a rendered preview.</div>
            ) : preview.isLoading && !preview.data ? (
              <div className="label text-paper-trace p-6">Rendering…</div>
            ) : preview.data ? (
              <div className="space-y-3">
                <div>
                  <div className="label text-paper-faint">Subject</div>
                  <div className="font-mono text-[13px] text-paper">{preview.data.subject}</div>
                </div>
                <div>
                  <div className="label text-paper-faint mb-1">HTML</div>
                  {/* Scripts in a template never run in the console's origin
                      (security review 2026-09-30). allow-same-origin WITHOUT
                      allow-scripts is inert: it only lets handlePreviewLoad
                      read the height. Never add allow-scripts here. */}
                  <iframe
                    ref={previewIframeRef}
                    sandbox="allow-same-origin"
                    srcDoc={preview.data.html}
                    onLoad={handlePreviewLoad}
                    style={{ height: `${previewHeight}px` }}
                    className="w-full border border-ink-500 bg-white"
                    title="Preview"
                  />
                </div>
                {preview.data.text && (
                  <div>
                    <div className="label text-paper-faint mb-1">Text</div>
                    <pre className="font-mono text-[11px] text-paper-dim whitespace-pre-wrap bg-ink-700/40 p-3 border border-ink-500">
                      {preview.data.text}
                    </pre>
                  </div>
                )}
              </div>
            ) : (
              <div className="label text-radar-red p-6">
                Preview error: {describeError(preview.error)}
              </div>
            )}
          </Panel>
        </div>
      </div>

      {/* Test-send modal */}
      {testOpen && (
        <div
          className="fixed inset-0 bg-ink-900/80 flex items-center justify-center z-50 p-6"
          onClick={() => setTestOpen(false)}
        >
          <div
            className="bg-ink-800 border border-ink-500 w-full max-w-[480px] p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="label-amber">Send test email</div>
            <Field
              label="To email"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="you@example.com"
            />
            <div className="font-mono text-[11px] text-paper-dim">
              If this email matches a real subscriber, their actual data
              (name, renewal link, subscription status) populates the merge
              variables — so the test looks exactly like a production send.
              Otherwise sample data is used. Sends directly via Mailgun —
              does not affect campaign history.
            </div>
            {testError && (
              <div className="font-mono text-[11px] text-radar-red">{testError}</div>
            )}
            {sendTest.isSuccess && (
              <div className="font-mono text-[11px] text-radar-green space-y-1">
                <div>
                  ✓ queued — Mailgun id {sendTest.data?.mailgun_message_id ?? 'unknown'}
                </div>
                {sendTest.data?.data_source === 'real_full' && (
                  <div className="text-paper-dim">
                    Used real subscriber #{sendTest.data.subscriber_id} +
                    subscription #{sendTest.data.subscription_id} for this publication.
                    {sendTest.data.personalized_links && ' Fresh personalized sign-in links were issued.'}
                  </div>
                )}
                {sendTest.data?.data_source === 'real_subscriber_only' && (
                  <div className="text-amber-glow">
                    Found subscriber #{sendTest.data.subscriber_id} but no
                    subscription for this template's publication — used
                    subscriber data with empty subscription fields.
                    {sendTest.data.personalized_links && ' Fresh personalized sign-in links were issued.'}
                  </div>
                )}
                {sendTest.data?.data_source === 'fake' && (
                  <div className="text-paper-dim">
                    No subscriber matched — used sample data.
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center justify-end gap-3 pt-2">
              <Button variant="outline" size="sm" onClick={() => setTestOpen(false)}>
                Close
              </Button>
              <Button
                size="sm"
                disabled={sendTest.isPending || !testEmail.includes('@')}
                onClick={() => sendTest.mutate()}
              >
                {sendTest.isPending ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function safeParseJson(s: string): Record<string, unknown> {
  try {
    return JSON.parse(s || '{}')
  } catch {
    return {}
  }
}

function describeError(err: unknown): string | null {
  if (!err) return null
  if (err instanceof ApiError) {
    if (typeof err.detail === 'object' && err.detail !== null) {
      const d = (err.detail as Record<string, unknown>).detail
      if (typeof d === 'string') return d
    }
    return err.message
  }
  return (err as Error).message ?? 'unknown error'
}
